import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps, GoogleMapUnavailable } from "@/lib/mapProvider";

const STATUS_COLOR = {
  active: "#00E676",
  pending_install: "#4DA6FF",
  paused: "#8B949E",
  suspended: "#FFB020",
  disabled: "#FF5252",
  terminated: "#FF5252",
};

function escapeHtml(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

/** Google Maps JS API equivalent of the Leaflet marker layer in
 * PppoeMap.js — one colored marker per account, an info window with a
 * "View profile" link on click. */
export default function GoogleAccountsMap({ apiKey, accounts, center, zoom = 12, height, onSelect }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const infoWindowRef = useRef(null);
  const markersRef = useRef([]);
  const onSelectRef = useRef(onSelect);
  const [status, setStatus] = useState("loading");

  onSelectRef.current = onSelect;

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps(apiKey)
      .then((maps) => {
        if (cancelled || !containerRef.current) return;
        mapRef.current = new maps.Map(containerRef.current, {
          center: { lat: center[0], lng: center[1] },
          zoom,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
        });
        infoWindowRef.current = new maps.InfoWindow();
        setStatus("ready");
      })
      .catch((e) => setStatus(e.message === "NO_KEY" ? "no_key" : "error"));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status !== "ready" || !window.google) return;
    const maps = window.google.maps;
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = accounts.map((a) => {
      const marker = new maps.Marker({
        position: { lat: a.gps_lat, lng: a.gps_lng },
        map: mapRef.current,
        title: a.pppoe_username,
        icon: {
          path: maps.SymbolPath.CIRCLE,
          scale: 7,
          fillColor: STATUS_COLOR[a.status] || "#8B949E",
          fillOpacity: 1,
          strokeColor: "rgba(0,0,0,0.35)",
          strokeWeight: 2,
        },
      });
      marker.addListener("click", () => {
        infoWindowRef.current.setContent(`
          <div style="font-family:monospace;font-size:12px">
            <div style="font-weight:bold">${escapeHtml(a.pppoe_username)}</div>
            <div>${escapeHtml(a.full_name || a.phone)}</div>
            <div style="text-transform:uppercase;font-size:10px;letter-spacing:0.05em;margin-top:4px">${escapeHtml(a.status.replace("_", " "))}</div>
            <button type="button" data-view-account="${a.id}" style="margin-top:8px;text-decoration:underline;font-size:11px;background:none;border:none;padding:0;cursor:pointer;color:inherit">View profile →</button>
          </div>
        `);
        infoWindowRef.current.open(mapRef.current, marker);
        maps.event.addListenerOnce(infoWindowRef.current, "domready", () => {
          const btn = document.querySelector(`[data-view-account="${a.id}"]`);
          if (btn) btn.addEventListener("click", () => onSelectRef.current(a.id));
        });
      });
      return marker;
    });
  }, [status, accounts]);

  if (status === "no_key" || status === "error") return <GoogleMapUnavailable height={height} status={status} />;
  return <div ref={containerRef} style={{ height: height || "100%", width: "100%" }} />;
}
